import React, { useEffect, useContext, useReducer } from 'react';
import reducer from '../reducers/cart_reducer';
import {
  ADD_TO_CART,
  REMOVE_CART_ITEM,
  TOGGLE_CART_ITEM_AMOUNT,
  CLEAR_CART,
  COUNT_CART_TOTALS,
  UPDATE_CART_VARIANT,
} from '../actions';
import { buildContents, sumItems } from '../utils/metaPixel';

const getLocalStorage = () => {
  let cart = localStorage.getItem('cart');

  if (cart) {
    return JSON.parse(cart);
  } else {
    return [];
  }
};

const initialState = {
  cart: getLocalStorage(),
  total_items: 0,
  total_amount: 0,
  shipping_fee: 0, // Free shipping
};

const CartContext = React.createContext();

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addToCart = (id, color, size, amount, product, variantSelections) => {
    dispatch({
      type: ADD_TO_CART,
      payload: { id, color, size, amount, product, variantSelections },
    });
    // Track AddToCart with Meta Pixel (single source of truth — the product
    // page no longer fires its own, which was double-counting).
    // Note on Advanced Matching: at add-to-cart the customer hasn't filled the
    // checkout form yet, so there is no PII (email/phone/name) to attach here.
    // Meta's automatic Advanced Matching still enriches this event from any
    // form data present; manual matching only becomes possible at Purchase.
    if (window.fbq) {
      const contents = buildContents([{ id, price: product.price, quantity: amount }]);
      // eventID makes this event dedup-ready (consistent with Purchase) in case
      // an AddToCart is ever mirrored server-side.
      const eventId = `atc_${id}_${Date.now()}`;
      window.fbq('track', 'AddToCart', {
        content_name: product.name,
        content_ids: contents.map((c) => c.id),
        content_type: 'product',
        contents,
        num_items: sumItems(contents),
        value: Number(product.price) * Number(amount) || 0,
        currency: 'TND',
      }, {
        eventID: eventId,
      });
    }
  };

  const removeItem = (id) => {
    dispatch({ type: REMOVE_CART_ITEM, payload: id });
  };

  const toggleAmount = (id, value) => {
    dispatch({ type: TOGGLE_CART_ITEM_AMOUNT, payload: { id, value } });
  };

  const updateCartVariant = (oldItemId, variantName, newValue) => {
    dispatch({
      type: UPDATE_CART_VARIANT,
      payload: { oldItemId, variantName, newValue },
    });
  };

  const clearCart = () => {
    dispatch({ type: CLEAR_CART });
  };

  useEffect(() => {
    dispatch({ type: COUNT_CART_TOTALS });
    localStorage.setItem('cart', JSON.stringify(state.cart));
  }, [state.cart]);

  return (
    <CartContext.Provider
      value={{ ...state, addToCart, removeItem, toggleAmount, updateCartVariant, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};
// make sure use
export const useCartContext = () => {
  return useContext(CartContext);
};
